import * as THREE from 'three';

/**
 * Create a THREE.js Canvas Texture from a sport icon
 * @param {string} iconType - Type of icon (soccer, basketball, tennis, football, hockey)
 * @param {string} color - Icon color (hex or rgb)
 * @param {number} size - Canvas size in pixels
 * @returns {THREE.CanvasTexture}
 */
export function createSportIconTexture(iconType, color = '#ffffff', size = 64) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  // Clear with transparent background
  ctx.clearRect(0, 0, size, size);

  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, size / 32);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  const radius = size / 3;
  const cx = size / 2;
  const cy = size / 2;

  switch (iconType) {
    case 'soccer':
      // Draw soccer ball with hexagon/pentagon pattern
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
      // Central hexagon
      for (let i = 0; i < 6; i++) {
        const angle = (i * Math.PI * 2) / 6;
        const x = cx + Math.cos(angle) * (radius * 0.5);
        const y = cy + Math.sin(angle) * (radius * 0.5);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.stroke();
      break;

    case 'basketball':
      // Draw basketball with curved lines
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
      // Curved lines on ball
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx, cy - radius);
      ctx.lineTo(cx, cy + radius);
      ctx.stroke();
      break;

    case 'tennis':
      // Draw tennis ball with curved seam
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
      // S-curve seam
      ctx.beginPath();
      ctx.arc(cx - radius * 0.3, cy, radius * 0.4, 0, Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx + radius * 0.3, cy, radius * 0.4, Math.PI, Math.PI * 2);
      ctx.stroke();
      break;

    case 'football':
      // Draw football/rugby ball (oval)
      ctx.beginPath();
      ctx.ellipse(cx, cy, radius, radius * 0.6, 0, 0, Math.PI * 2);
      ctx.stroke();
      // Laces
      for (let i = 0; i < 3; i++) {
        const offset = (i - 1) * 8;
        ctx.beginPath();
        ctx.moveTo(cx, cy - radius * 0.4 + offset);
        ctx.lineTo(cx, cy + radius * 0.4 + offset);
        ctx.stroke();
      }
      break;

    case 'hockey':
      // Draw hockey puck
      ctx.beginPath();
      ctx.ellipse(cx, cy, radius * 1.2, radius * 0.4, 0, 0, Math.PI * 2);
      ctx.stroke();
      // Center line
      ctx.beginPath();
      ctx.moveTo(cx - radius * 1.2, cy);
      ctx.lineTo(cx + radius * 1.2, cy);
      ctx.stroke();
      break;

    case 'baseball':
      // Draw baseball with stitching
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
      // Stitching pattern
      ctx.beginPath();
      ctx.arc(cx, cy, radius * 0.7, 0, Math.PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, radius * 0.7, Math.PI, Math.PI * 2);
      ctx.stroke();
      break;

    default:
      // Default: simple circle with star
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.stroke();
      for (let i = 0; i < 5; i++) {
        const angle = (i * Math.PI * 2) / 5 - Math.PI / 2;
        const x = cx + Math.cos(angle) * radius * 0.6;
        const y = cy + Math.sin(angle) * radius * 0.6;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

export const sportIconTypes = [
  'soccer',
  'basketball',
  'tennis',
  'football',
  'hockey',
  'baseball',
];
